import {
  buildShortLinkTargetPath,
  shortLinkUnavailableMessage,
  type ResolveShortLinkInput,
  type ResolveShortLinkResult,
  type ShortLinkUnavailableReason,
} from '@citizen-walk/shared-types';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { functions } from '../../lib/firebase';
import { useAnonymousAuth } from '../auth';

/**
 * 短縮アドレス `{origin}/s/:code` の受け口 (docs/26)。
 *
 * イベント会場で参加者が **PC のアドレスバーに手で打ち込む**ための短い入口。ここでは
 * `resolveShortLink` callable で「今の参加 URL (urlToken)」を引き当て、通常の入口
 * (`/m/:urlToken`、セッション指定つきなら参加 QR と同じ `/m/:urlToken/join/:sessionId`) へ
 * **置換遷移**する。以降の体験は QR から入った人と完全に同じ。
 *
 * 置換遷移 (`replace: true`) にしているのは、ブラウザの戻るでこの中継画面に戻ってきて
 * 再解決が走るのを防ぐため (SessionJoinPage と同じ考え方)。
 *
 * 失敗時は理由に応じた日本語だけを出す (内部キーは画面に出さない、CLAUDE.md のルール)。
 * 「期限切れ」と「停止中」は配布元が再有効化すれば**同じアドレスのまま**復活するので、
 * 参加者には「配布元にお問い合わせください」と案内する。
 */

/** 解決失敗の理由を callable のエラーから取り出す (details.reason は resolveShortLink が付与)。 */
export function readUnavailableReason(error: unknown): ShortLinkUnavailableReason | null {
  const details = (error as { details?: unknown })?.details;
  const reason = (details as { reason?: unknown } | undefined)?.reason;
  return reason === 'not-found' ||
    reason === 'expired' ||
    reason === 'disabled' ||
    reason === 'district-archived'
    ? reason
    : null;
}

type PageState =
  | { status: 'resolving' }
  | { status: 'error'; message: string }
  | { status: 'done' };

export function ShortLinkPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const authState = useAnonymousAuth();
  const user = authState.status === 'authenticated' ? authState.user : null;
  const authError = authState.status === 'error';
  const [state, setState] = useState<PageState>({ status: 'resolving' });

  // 呼び出し前に確定する失敗 (URL に code が無い / サインインできない) は描画時に判定する。
  // effect の中で同期 setState すると余分な再描画を招くため (react-hooks/set-state-in-effect)。
  const precheckMessage = !code
    ? shortLinkUnavailableMessage('not-found')
    : authError
      ? 'ネットワークに接続できませんでした。通信環境をご確認のうえ、再度お試しください。'
      : null;

  useEffect(() => {
    if (!code || authError || !user) return;

    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable<ResolveShortLinkInput, ResolveShortLinkResult>(
          functions,
          'resolveShortLink',
        );
        const res = await fn({ code });
        if (cancelled) return;
        setState({ status: 'done' });
        navigate(buildShortLinkTargetPath(res.data.urlToken, res.data.sessionId), {
          replace: true,
        });
      } catch (e) {
        if (cancelled) return;
        const reason = readUnavailableReason(e);
        setState({
          status: 'error',
          message: reason
            ? shortLinkUnavailableMessage(reason)
            : 'アドレスを確認できませんでした。通信環境をご確認のうえ、再度お試しください。',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, user, authError, navigate]);

  const errorMessage = precheckMessage ?? (state.status === 'error' ? state.message : null);

  if (errorMessage) {
    return (
      <main className="flex flex-col items-center justify-center h-dvh bg-white px-6 text-center">
        <span className="text-4xl mb-3" aria-hidden>
          🔗
        </span>
        <h1 className="text-lg font-bold text-slate-900 mb-3">このアドレスは開けませんでした</h1>
        <p className="text-sm text-slate-700 mb-2 max-w-sm leading-relaxed">{errorMessage}</p>
        {code ? (
          <p className="text-xs text-slate-400 mb-8 break-all">入力されたアドレス: /s/{code}</p>
        ) : null}
        <Link
          to="/"
          className="inline-block bg-primary-600 text-white text-sm font-medium py-3 px-5 rounded-md hover:bg-primary-700"
        >
          トップへ
        </Link>
      </main>
    );
  }

  return (
    <main
      className="flex flex-col items-center justify-center h-dvh bg-white px-6 text-center"
      aria-busy="true"
    >
      <p className="text-sm text-slate-600">アドレスを確認しています…</p>
    </main>
  );
}
